import React, { useRef, useState } from "react";
import "./PainAssessmentBodyDiagram.css";
import { pointsToPathString } from "../../../../patients/components/painAssessment/pain-assesment.utils";

// Import SVG components
import { ReactComponent as FemaleAnterior } from "../../../../../assets/images/painAssesment/female_anterior.svg";
import { ReactComponent as FemalePosterior } from "../../../../../assets/images/painAssesment/female_posterior.svg";
import { ReactComponent as FemaleLeft } from "../../../../../assets/images/painAssesment/female_left.svg";
import { ReactComponent as FemaleRight } from "../../../../../assets/images/painAssesment/female_right.svg";
import { ReactComponent as MaleAnterior } from "../../../../../assets/images/painAssesment/male_anterior.svg";
import { ReactComponent as MalePosterior } from "../../../../../assets/images/painAssesment/male_posterior.svg";
import { ReactComponent as MaleLeft } from "../../../../../assets/images/painAssesment/male_left.svg";
import { ReactComponent as MaleRight } from "../../../../../assets/images/painAssesment/male_right.svg";

const PainAssessmentBodyDiagram = ({
  assessment,
  orientation,
  orientationName,
}) => {
  const diagramRef = useRef(null);
  const [hoveredPathInfo, setHoveredPathInfo] = useState(null);

  const getStrokeWidth = pathObj => {
    const width = Number(pathObj?.strokeWidth);
    return width > 0 ? width : 3;
  };

  // Get SVG components based on gender
  const getSvgComponent = (gender, orientation) => {
    const components = {
      Male: [MaleAnterior, MaleLeft, MalePosterior, MaleRight],
      Female: [FemaleAnterior, FemaleLeft, FemalePosterior, FemaleRight],
    };
    return components[gender]?.[orientation] || components.Male[orientation];
  };

  // Get paths for specific orientation
  const getPathsForOrientation = orientation => {
    if (!assessment?.allPaths) return [];
    return assessment.allPaths.filter(
      pathObj => pathObj.orientation === orientation
    );
  };

  const SvgComponent = getSvgComponent(
    assessment?.gender || "Male",
    orientation
  );
  const pathsForOrientation = getPathsForOrientation(orientation);

  const handlePathHover = (pathObj, event) => {
    if (!pathObj || !pathObj.painLevel) return;

    const rect = diagramRef.current?.getBoundingClientRect();
    const offsetX = (event?.clientX ?? 0) - (rect?.left ?? 0);
    const offsetY = (event?.clientY ?? 0) - (rect?.top ?? 0);

    setHoveredPathInfo({
      painLevel: pathObj.painLevel,
      painType: pathObj.painType,
      position: {
        x: rect ? Math.max(0, offsetX) : 0,
        y: rect ? Math.max(0, offsetY) : 0,
      },
    });
  };

  const handlePathLeave = () => setHoveredPathInfo(null);

  return (
    <div
      className="pain-assessment-body-diagram"
      ref={diagramRef}
      onMouseLeave={handlePathLeave}
    >
      {/* SVG container with direct drawing - SAME AS CAROUSEL */}
      <div className="pain-body-diagram-svg-container">
        <svg
          className="pain-body-diagram-svg-with-paths"
          viewBox="0 0 215 584"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%" }}
        >
          {/* Original anatomy SVG content - SAME AS CAROUSEL */}
          <SvgComponent />

          {/* Pain paths drawn directly on the anatomy SVG - SAME AS CAROUSEL */}
          {pathsForOrientation.map((pathObj, index) => (
            <path
              key={`pain-path-${orientation}-${index}`}
              d={pointsToPathString(pathObj.path)}
              stroke={pathObj.color}
              fill="none"
              strokeWidth={getStrokeWidth(pathObj)}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
              vectorEffect="non-scaling-stroke"
              className="pain-recorded-path"
              onMouseEnter={e => handlePathHover(pathObj, e)}
              onMouseMove={e => handlePathHover(pathObj, e)}
              onMouseLeave={handlePathLeave}
              style={{ pointerEvents: "stroke", cursor: "pointer" }}
            />
          ))}
        </svg>
      </div>
      {hoveredPathInfo && (
        <div
          className="pain-path-tooltip"
          style={{
            left: hoveredPathInfo.position?.x || 0,
            top: hoveredPathInfo.position?.y || 0,
          }}
        >
          <div className="pain-path-tooltip-level">
            Pain level {hoveredPathInfo.painLevel}
          </div>
          <div className="pain-path-tooltip-type">
            Type: {hoveredPathInfo.painType || "Not specified"}
          </div>
        </div>
      )}
    </div>
  );
};

export default PainAssessmentBodyDiagram;
